/**
 * Casper Repair Package Modal (LIVE)
 *
 * Opens a final add-on checklist when the customer presses Add to Cart. It
 * mirrors every selection to the existing Casper and assembly inputs, then
 * resumes the theme's proven cart flow.
 */

(function () {
	if (window.__PARTIFY_DESKTOP_ADDON_SPLIT) return;
	const SLUG = "casper-add-on-modal";
	const STYLE_ID = "partify-casper-add-on-modal-styles";
	const DIALOG_ID = "partify-casper-package-dialog";
	const ATC_SELECTOR =
		".add-to-cart, .add-to-cart-for-unpainted, .sticky-add-to-cart, [data-add-to-cart], .product-form__submit, button[name='add']";
	const DEFAULT_ADD_ON_COMMIT_TIMEOUT_MS = 12000;

	let initialized = false;
	let lastTrigger = null;
	let assemblyHome = null;
	let selectionSnapshot = null;
	let preserveSelectionsOnNextOpen = false;
	let resumeNativeSubmission = false;
	let fitmentContinuationApproved = false;
	let previewImages = [];
	let previewImageIndex = 0;
	let previewTrigger = null;
	let activeCommitAttempt = null;
	let externalModalRequest = null;
	let reportSelection = { selected: false, vin: "", confirmed: false };
	let reportRevision = 0;
	let completedCartItems = [];

	function reportConfig() {
		return (window.__PARTIFY_BUNDLE || {}).vehicleHistory;
	}

	function reportExperimentEnabled() {
		// The existing modal can be live or forced on by the preview QA toggle.
		// Neither condition assigns this separate report experiment to B.
		return window.__PARTIFY_VEHICLE_HISTORY_REPORT_ADDON === "B";
	}

	function reportText(key) {
		const text = document.createElement("textarea");
		text.innerHTML = (reportConfig() && reportConfig().strings[key]) || "";
		return text.value;
	}

	function reportAvailable() {
		const config = reportConfig();
		const currency = window.Shopify?.currency?.active || window.shopCurrency;
		return !!(reportExperimentEnabled() && config && config.available && currency === "USD");
	}

	function reportError() {
		if (!reportSelection.selected) return "";
		if (!reportAvailable()) return reportText("unavailable");
		if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(reportSelection.vin)) return reportText("invalidVin");
		if (!reportSelection.confirmed) return reportText("confirmVin");
		return "";
	}

	function reportMarkup() {
		const config = reportConfig();
		if (!config || (window.Shopify?.currency?.active || window.shopCurrency) !== "USD") return "";
		return '<article class="casper-package-modal__product-card" data-casper-report-card' + (reportExperimentEnabled() ? '' : ' hidden') + '>' +
			'<button type="button" class="casper-package-modal__media" data-casper-package-media aria-label="' + escapeHtml(reportText("title")) + '"><img src="' + escapeHtml(config.imageUrl + '&width=320') + '" data-full-size-src="' + escapeHtml(config.imageUrl) + '" alt="' + escapeHtml(reportText("title")) + '" width="112" height="96" loading="lazy"></button>' +
			'<div class="casper-package-modal__product-copy"><span class="casper-package-modal__badge">' + escapeHtml(reportText("badge")) +
			'</span><strong>' + escapeHtml(reportText("title")) + '</strong><span>' + escapeHtml(reportText("description")) + '</span></div>' +
			'<div class="casper-package-modal__product-actions"><strong>+' + escapeHtml(money(config.priceCents)) +
			'</strong><label class="casper-package-modal__toggle" aria-label="' + escapeHtml(reportText("toggle")) +
			'"><input type="checkbox" data-casper-report-toggle aria-controls="casper-report-fields"><span aria-hidden="true"></span></label></div>' +
			'<div class="casper-package-modal__report-details"><p>' + escapeHtml(reportText("access")) + '</p><p>' + escapeHtml(reportText("discounts")) + '</p>' +
			'<p data-casper-report-unavailable hidden>' + escapeHtml(reportText("unavailable")) + '</p>' +
			'<div id="casper-report-fields" hidden><label for="casper-report-vin">' + escapeHtml(reportText("vin")) + '</label>' +
			'<input id="casper-report-vin" data-casper-report-vin type="text" maxlength="17" autocomplete="off" autocapitalize="characters" spellcheck="false" aria-describedby="casper-report-vin-help casper-report-error">' +
			'<p id="casper-report-vin-help">' + escapeHtml(reportText("vinHelp")) + '</p>' +
			'<label class="casper-package-modal__report-confirm"><input type="checkbox" data-casper-report-confirm><span>' + escapeHtml(reportText("confirm")) + '</span></label>' +
			'<p id="casper-report-error" data-casper-report-error role="status"></p></div></div></article>';
	}

	function syncReportUi(dialog) {
		const toggle = dialog.querySelector("[data-casper-report-toggle]");
		if (!toggle) return;
		dialog.querySelector("[data-casper-report-card]").hidden = !reportExperimentEnabled();
		if (!reportExperimentEnabled() && (reportSelection.selected || reportSelection.vin || reportSelection.confirmed)) {
			reportSelection = { selected: false, vin: "", confirmed: false };
			reportRevision += 1;
		}
		toggle.disabled = !reportAvailable();
		toggle.checked = reportSelection.selected;
		toggle.setAttribute("aria-expanded", String(reportSelection.selected));
		dialog.querySelector("[data-casper-report-unavailable]").hidden = reportAvailable();
		dialog.querySelector("#casper-report-fields").hidden = !reportSelection.selected;
		dialog.querySelector("[data-casper-report-vin]").value = reportSelection.vin;
		dialog.querySelector("[data-casper-report-confirm]").checked = reportSelection.confirmed;
		dialog.querySelector("[data-casper-report-error]").textContent = reportError();
	}

	async function selectedReportItems() {
		if (!reportSelection.selected) return [];
		const error = reportError();
		if (error) throw new Error(error);
		const config = reportConfig();
		const vin = reportSelection.vin;
		const revision = reportRevision;
		const root = window.Shopify?.routes?.root || "/";
		const read = async function (path) {
			const response = await fetch(root + path, { credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(8000) });
			if (!response.ok) throw new Error(reportText("unavailable"));
			return response.json();
		};
		const [product, cart] = await Promise.all([read("products/" + config.handle + ".js"), read("cart.js")]);
		if (reportRevision !== revision || reportError()) throw new Error(reportText("confirmVin"));
		const variant = product.variants?.find(item => String(item.id) === String(config.variantId));
		if (cart.currency !== "USD" || !Array.isArray(cart.items) || !variant?.available || variant.sku !== config.sku || variant.requires_shipping !== false || variant.price !== config.priceCents) {
			throw new Error(reportText("unavailable"));
		}
		const existing = cart.items?.filter(item => String(item.variant_id) === String(config.variantId) && String(item.properties?.VIN || "").trim().toUpperCase() === vin) || [];
		if (existing.length) {
			if (existing.length !== 1 || existing[0].quantity !== 1 || existing[0].properties.VIN !== vin || existing[0].properties._vhr_schema !== "1" || existing[0].properties._vhr_confirmed_vin !== vin) {
				throw new Error(reportText("reviewCart"));
			}
			if (!/^[a-f0-9]{64}$/.test(existing[0].properties._digital_access_key || "")) {
				const response = await fetch(root + "cart/change.js", { method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(8000), headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: existing[0].key, quantity: 1, properties: { ...existing[0].properties, _digital_access_key: Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, "0")).join("") } }) });
				if (!response.ok) throw new Error(reportText("reviewCart"));
			}
			return [];
		}
		return [{ id: String(config.variantId), quantity: 1, properties: {
			VIN: vin, _vhr_schema: "1", _vhr_confirmed_vin: vin,
			_vhr_addon_test: SLUG, _vhr_addon_variant: "B",
			_digital_access_key: Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, "0")).join("")
		} }];
	}

	function bindReportEvents(dialog) {
		const toggle = dialog.querySelector("[data-casper-report-toggle]");
		if (!toggle) return;
		toggle.addEventListener("change", function () {
			reportSelection.selected = toggle.checked;
			reportRevision += 1;
			syncSelectionUi(dialog);
			track("casper_package_report_toggled", { selected: toggle.checked });
		});
		dialog.querySelector("[data-casper-report-vin]").addEventListener("input", function (event) {
			reportSelection.vin = event.target.value.trim().toUpperCase();
			reportSelection.confirmed = false;
			reportRevision += 1;
			syncReportUi(dialog);
		});
		dialog.querySelector("[data-casper-report-confirm]").addEventListener("change", function (event) {
			reportSelection.confirmed = event.target.checked;
			reportRevision += 1;
			syncReportUi(dialog);
		});
		window.__partifyGetSelectedVehicleHistoryAddOn = selectedReportItems;
		window.__partifyVehicleHistoryAddOnSelected = function () { return reportExperimentEnabled() && reportSelection.selected; };
		window.__partifyValidateVehicleHistoryAddOn = function (items) {
			const report = items.find(item => item.properties?._vhr_schema === "1");
			if (report && (!reportSelection.selected || reportError() || report.properties.VIN !== reportSelection.vin)) {
				throw new Error(reportText("confirmVin"));
			}
		};
		document.addEventListener("partify:vehicle-history-addon-assigned", function () {
			syncSelectionUi(dialog);
		});
	}

	function intendedLineKey(item) {
		const properties = Object.entries((item && item.properties) || {})
			.filter(function (entry) {
				return entry[0] !== "Bundled" && entry[0] !== "EasySearch";
			})
			.sort(function (left, right) {
				return left[0].localeCompare(right[0]);
			});
		return JSON.stringify({
			id: String((item && item.id) || ""),
			sellingPlan: String((item && (item.selling_plan || item.sellingPlan)) || ""),
			properties: properties,
		});
	}

	function dedupeIntendedLines(items) {
		const seen = new Set();
		return (Array.isArray(items) ? items : []).filter(function (item) {
			const key = intendedLineKey(item);
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	}

	function track(eventName, properties) {
		try {
			window.igEvents = window.igEvents || [];
			window.igEvents.push({
				event: eventName,
				properties: Object.assign(
					{
						feature: "casper-repair-package-modal",
						page_path: window.location.pathname,
						page_url: window.location.href,
					},
					properties || {},
				),
			});
		} catch (_) {}
	}

	function escapeHtml(value) {
		return String(value || "").replace(/[&<>"']/g, function (character) {
			return {
				"&": "&amp;",
				"<": "&lt;",
				">": "&gt;",
				'"': "&quot;",
				"'": "&#39;",
			}[character];
		});
	}

	function money(cents) {
		const value = Number(cents);
		if (!Number.isFinite(value) || value <= 0) return "Included";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD",
		}).format(value / 100);
	}

	function inputs() {
		return {
			clip: document.querySelector('[data-casper-bundle] [data-item-input="clip"]'),
			paint: document.querySelector('[data-casper-bundle] [data-item-input="paint"]'),
			single: document.querySelector('input[name="bundle_mode"][value="single"]'),
			kit: document.querySelector('input[name="bundle_mode"][value="kit"]'),
		};
	}

	function sourceSections() {
		return [
			document.getElementById("bundle-options"),
			document.querySelector("[data-casper-bundle]"),
		].filter(Boolean);
	}

	function hideSourceSections() {
		sourceSections().forEach(function (section) {
			section.dataset.casperModalSource = "true";
			section.setAttribute("aria-hidden", "true");
			section.hidden = true;
			section.style.setProperty("display", "none", "important");
		});
	}

	function addOnState(key) {
		const control = inputs()[key];
		const fallback =
			key === "clip"
				? "The auto clip kit is unavailable for this part."
				: "Touch-up paint is unavailable until a matching paint code is selected.";
		if (!control) return { exists: false, ready: false, reason: fallback };
		if (!control.disabled) return { exists: true, ready: true, reason: "" };
		const item = control.closest("[data-item]");
		const shipping = item && item.querySelector("[data-item-ship]");
		return {
			exists: true,
			ready: false,
			reason: String((shipping && shipping.textContent) || "").trim() || fallback,
		};
	}

	function setChecked(control, checked) {
		if (!control || (checked && control.disabled)) return false;
		if (control.checked !== checked) {
			control.checked = checked;
			control.dispatchEvent(new Event("change", { bubbles: true }));
		}
		return true;
	}

	function setAssemblyMode(mode) {
		const control = inputs()[mode];
		if (!control) return mode !== "kit";
		if (!control.checked) {
			control.checked = true;
			control.dispatchEvent(new Event("change", { bubbles: true }));
		}
		return true;
	}

	function assemblyToggleInputs() {
		return Array.from(document.querySelectorAll("#kit-items .kit-item__toggle-input"));
	}

	function selectedAssemblyCount() {
		return assemblyToggleInputs().filter(function (toggle) {
			return toggle.checked;
		}).length;
	}

	function addOnCommitTimeoutMs() {
		// The override is intentionally available for automated QA. Production uses the
		// conservative default so a stalled third-party/cart request never strands the
		// customer on a disabled "Adding to cart..." button.
		const configured = Number(window.__PARTIFY_CASPER_ADD_ON_MODAL_COMMIT_TIMEOUT_MS);
		return Number.isFinite(configured) && configured >= 100
			? configured
			: DEFAULT_ADD_ON_COMMIT_TIMEOUT_MS;
	}

	function fitmentGateWillBlock() {
		if (fitmentContinuationApproved) return false;
		return !!(
			window.__pdpFitmentGate &&
			typeof window.__pdpFitmentGateIsReady === "function" &&
			!window.__pdpFitmentGateIsReady()
		);
	}

	function captureSelections() {
		const control = inputs();
		return {
			report: Object.assign({}, reportSelection),
			clip: !!(control.clip && control.clip.checked),
			paint: !!(control.paint && control.paint.checked),
			mode: control.kit && control.kit.checked ? "kit" : "single",
			assembly: assemblyToggleInputs().map(function (toggle) {
				return toggle.checked;
			}),
		};
	}

	function restoreSelections(snapshot) {
		if (!snapshot) return;
		reportSelection = Object.assign({}, snapshot.report);
		reportRevision += 1;
		const control = inputs();
		setChecked(control.clip, snapshot.clip);
		setChecked(control.paint, snapshot.paint);
		assemblyToggleInputs().forEach(function (toggle, index) {
			if (typeof snapshot.assembly[index] !== "boolean" || toggle.checked === snapshot.assembly[index]) return;
			toggle.checked = snapshot.assembly[index];
			toggle.dispatchEvent(new Event("change", { bubbles: true }));
		});
		setAssemblyMode(snapshot.mode);
	}

	function addOnMedia(key) {
		const image = document.querySelector(
			'[data-casper-bundle] [data-item="' + key + '"] [data-item-img]',
		);
		if (!image || !image.getAttribute("src")) {
			return '<span class="casper-package-modal__media is-fallback" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8.5h16l-1 10H5l-1-10Z"/><path d="M9 11.5h6"/><path d="M8 8.5 9.7 4h4.6L16 8.5"/></svg></span>';
		}
		return (
			'<span class="casper-package-modal__media" data-casper-package-media title="View larger product image"><img src="' +
			escapeHtml(image.getAttribute("src")) +
			'" alt="' +
			escapeHtml(image.getAttribute("alt") || "") +
			'"></span>'
		);
	}

	function addOnCardMarkup(key, letter, heading, description, price) {
		const state = addOnState(key);
		const control = inputs()[key];
		return (
			'<article class="casper-package-modal__product-card' +
			(state.ready ? "" : " is-unavailable") +
			'" data-casper-product-card="' +
			key +
			'">' +
			addOnMedia(key) +
			'<div class="casper-package-modal__product-copy"><span class="casper-package-modal__badge">Product ' +
			escapeHtml(letter) +
			"</span><strong>" +
			escapeHtml(heading) +
			'</strong><span data-casper-product-description>' +
			escapeHtml(state.ready ? description : state.reason) +
			"</span></div>" +
			'<div class="casper-package-modal__product-actions"><strong>+' +
			escapeHtml(price) +
			'</strong><label class="casper-package-modal__toggle" aria-label="Add ' +
			escapeHtml(heading) +
			'"><input type="checkbox" data-casper-package-toggle="' +
			key +
			'"' +
			(control && control.checked ? " checked" : "") +
			(state.ready ? "" : " disabled") +
			'><span aria-hidden="true"></span></label></div></article>'
		);
	}

	function buildDialog() {
		const cfg = window.__PARTIFY_BUNDLE || {};
		const dialog = document.createElement("dialog");
		dialog.id = DIALOG_ID;
		dialog.className = "casper-package-modal";
		dialog.setAttribute("data-casper-package-dialog", "");
		dialog.setAttribute("aria-labelledby", "casper-package-modal-title");
		dialog.innerHTML =
			'<div class="casper-package-modal__panel">' +
			'<header class="casper-package-modal__header"><div><span>Before you add to cart</span><h2 id="casper-package-modal-title">Complete your repair.</h2><p>Add any extras you want, or leave everything off and continue with just this part.</p></div><button type="button" class="casper-package-modal__close" data-casper-package-close aria-label="Close repair add-ons">&times;</button></header>' +
			'<div class="casper-package-modal__body" data-casper-package-modal-body>' +
			'<section class="casper-package-modal__add-ons" aria-labelledby="casper-package-addons-title"><div class="casper-package-modal__section-heading"><div><span>Optional add-ons</span><h3 id="casper-package-addons-title">Finish the job</h3><p>Turn on any add-on you want in this order.</p></div></div><div class="casper-package-modal__products">' +
			addOnCardMarkup(
				"clip",
				"A",
				"Auto Clip Kit",
				"240 OEM-grade clips and fasteners for a clean, rattle-free install.",
				money(cfg.kitPriceCents),
			) +
			addOnCardMarkup(
				"paint",
				"B",
				"Touch-Up Bottle Essential Kit",
				"Factory-matched touch-up paint and clearcoat for the finishing details.",
				money(cfg.paintPriceCents),
			) +
			reportMarkup() +
			"</div></section>" +
			'<section class="casper-package-modal__assembly" data-casper-package-assembly hidden aria-labelledby="casper-package-assembly-title"><div class="casper-package-modal__section-heading"><div><span>Product C</span><h3 id="casper-package-assembly-title">Assembly options</h3><p>Choose the individual parts you want to add to this repair.</p></div><span class="casper-package-modal__assembly-count" data-casper-package-assembly-count></span></div><div class="casper-package-modal__assembly-items" data-casper-package-assembly-items></div></section>' +
			"</div>" +
			'<footer class="casper-package-modal__footer"><p class="casper-package-modal__notice" data-casper-package-notice aria-live="polite" tabindex="-1">Choose any extras you want, then add everything to your cart.</p><button type="button" class="casper-package-modal__secondary" data-casper-package-close>Cancel</button><button type="button" class="casper-package-modal__primary" data-casper-package-confirm>Add selected to cart <span aria-hidden="true">&rarr;</span></button></footer>' +
			'<div class="casper-package-modal__image-preview" data-casper-package-image-preview hidden aria-hidden="true"><div class="casper-package-modal__image-preview-card" role="dialog" aria-modal="true" aria-labelledby="casper-package-image-preview-title"><div class="casper-package-modal__image-preview-heading"><span id="casper-package-image-preview-title" data-casper-package-image-preview-title>Product image</span><button type="button" class="casper-package-modal__image-preview-close" data-casper-package-image-preview-close aria-label="Close image preview">&times;</button></div><div class="casper-package-modal__image-preview-stage"><button type="button" class="casper-package-modal__image-preview-nav" data-casper-package-image-preview-previous aria-label="Previous product image">&larr;</button><img data-casper-package-image-preview-image alt=""><button type="button" class="casper-package-modal__image-preview-nav" data-casper-package-image-preview-next aria-label="Next product image">&rarr;</button></div><p class="casper-package-modal__image-preview-count" data-casper-package-image-preview-count></p></div></div>' +
			"</div>";
		return dialog;
	}

	function mountAssemblyOptions(dialog) {
		const kitItems = document.getElementById("kit-items");
		const section = dialog && dialog.querySelector("[data-casper-package-assembly]");
		const mount = dialog && dialog.querySelector("[data-casper-package-assembly-items]");
		if (!kitItems || !section || !mount) return false;
		if (!assemblyHome) assemblyHome = { parent: kitItems.parentNode, next: kitItems.nextSibling };
		mount.appendChild(kitItems);
		section.hidden = !kitItems.querySelector(".kit-item");
		return !section.hidden;
	}

	function unmountAssemblyOptions(dialog) {
		const kitItems = document.getElementById("kit-items");
		const section = dialog && dialog.querySelector("[data-casper-package-assembly]");
		if (kitItems && assemblyHome && assemblyHome.parent) {
			if (assemblyHome.next && assemblyHome.next.parentNode === assemblyHome.parent) {
				assemblyHome.parent.insertBefore(kitItems, assemblyHome.next);
			} else {
				assemblyHome.parent.appendChild(kitItems);
			}
		}
		assemblyHome = null;
		if (section) section.hidden = true;
	}

	function fullSizeImageSource(source) {
		return String(source || "").replace(
			/_(?:\d+)x(?:\d+)(?=\.(?:jpg|jpeg|png|webp)(?:\?|$))/i,
			"",
		);
	}

	function previewImageList(imageElements, label) {
		return Array.from(imageElements || [])
			.map(function (image) {
				const src = fullSizeImageSource(image.getAttribute("data-full-size-src") || image.currentSrc || image.getAttribute("src"));
				return src
					? { src: src, alt: image.getAttribute("alt") || label, label: label || "Product image" }
					: null;
			})
			.filter(Boolean);
	}

	function renderImagePreview(dialog) {
		const preview = dialog && dialog.querySelector("[data-casper-package-image-preview]");
		const image = previewImages[previewImageIndex];
		if (!preview || !image) return;
		const previewImage = preview.querySelector("[data-casper-package-image-preview-image]");
		const title = preview.querySelector("[data-casper-package-image-preview-title]");
		const count = preview.querySelector("[data-casper-package-image-preview-count]");
		const previous = preview.querySelector("[data-casper-package-image-preview-previous]");
		const next = preview.querySelector("[data-casper-package-image-preview-next]");
		if (previewImage) {
			previewImage.src = image.src;
			previewImage.alt = image.alt;
		}
		if (title) title.textContent = image.label;
		if (count) count.textContent = previewImages.length > 1 ? previewImageIndex + 1 + " of " + previewImages.length : "";
		if (previous) previous.hidden = previewImages.length < 2;
		if (next) next.hidden = previewImages.length < 2;
	}

	function openImagePreview(dialog, imageElements, label, trigger) {
		const preview = dialog && dialog.querySelector("[data-casper-package-image-preview]");
		previewImages = previewImageList(imageElements, label);
		if (!preview || !previewImages.length) return;
		previewImageIndex = 0;
		previewTrigger = trigger || null;
		preview.hidden = false;
		preview.setAttribute("aria-hidden", "false");
		renderImagePreview(dialog);
		const close = preview.querySelector("[data-casper-package-image-preview-close]");
		if (close) window.setTimeout(function () { close.focus(); }, 0);
		track("casper_package_image_previewed", { image_count: previewImages.length, source: label || "product" });
	}

	function closeImagePreview(dialog, restoreFocus) {
		const preview = dialog && dialog.querySelector("[data-casper-package-image-preview]");
		if (!preview || preview.hidden) return;
		preview.hidden = true;
		preview.setAttribute("aria-hidden", "true");
		const trigger = previewTrigger;
		previewImages = [];
		previewImageIndex = 0;
		previewTrigger = null;
		if (restoreFocus && trigger && document.contains(trigger) && typeof trigger.focus === "function") trigger.focus();
	}

	function moveImagePreview(dialog, direction) {
		if (previewImages.length < 2) return;
		previewImageIndex = (previewImageIndex + direction + previewImages.length) % previewImages.length;
		renderImagePreview(dialog);
	}

	function syncSelectionUi(dialog) {
		if (!dialog) return;
		syncReportUi(dialog);
		dialog.querySelectorAll("[data-casper-package-toggle]").forEach(function (toggle) {
			const key = toggle.dataset.casperPackageToggle;
			const control = inputs()[key];
			const state = addOnState(key);
			const card = toggle.closest("[data-casper-product-card]");
			const description = card && card.querySelector("[data-casper-product-description]");
			toggle.disabled = !state.ready;
			toggle.checked = !!(control && control.checked);
			if (card) card.classList.toggle("is-unavailable", !state.ready);
			if (description) {
				description.textContent = state.ready
					? description.dataset.defaultDescription || description.textContent
					: state.reason;
			}
		});

		const section = dialog.querySelector("[data-casper-package-assembly]");
		const kitItems = document.getElementById("kit-items");
		if (section && kitItems && assemblyHome) section.hidden = !kitItems.querySelector(".kit-item");
		const assemblyCount = selectedAssemblyCount();
		const count = dialog.querySelector("[data-casper-package-assembly-count]");
		const totalAssembly = assemblyToggleInputs().length;
		if (count) count.textContent = totalAssembly ? assemblyCount + " of " + totalAssembly + " selected" : "";

		const control = inputs();
		const selectedAddOns = Number(!!(control.clip && control.clip.checked)) + Number(!!(control.paint && control.paint.checked));
		const selectedCount = selectedAddOns + assemblyCount + Number(reportSelection.selected);
		const notice = dialog.querySelector("[data-casper-package-notice]");
		const confirm = dialog.querySelector("[data-casper-package-confirm]");
		if (notice) {
			notice.removeAttribute("data-cart-error");
			notice.textContent = selectedCount
				? selectedCount + " optional " + (selectedCount === 1 ? "item is" : "items are") + " selected for this order."
				: "No optional items selected - you will add just this part.";
		}
		if (confirm && !confirm.disabled) {
			confirm.innerHTML = selectedCount
				? "Add selected to cart <span aria-hidden=\"true\">&rarr;</span>"
				: "Add part to cart <span aria-hidden=\"true\">&rarr;</span>";
		}
	}

	function rememberDescriptions(dialog) {
		dialog.querySelectorAll("[data-casper-product-description]").forEach(function (description) {
			description.dataset.defaultDescription = description.textContent || "";
		});
	}

	function openDialog(trigger, source) {
		const dialog = document.getElementById(DIALOG_ID);
		if (!dialog || dialog.open || activeCommitAttempt || window.__partifyAddOnStyles?.pending()) return;
		lastTrigger = trigger || document.activeElement;
		selectionSnapshot = captureSelections();
		if (!source && window.__partifyAddOnStyles?.active()) {
			setChecked(inputs().clip, false);
			setChecked(inputs().paint, false);
			if (!inputs().kit?.checked) assemblyToggleInputs().forEach(function (toggle) {
				if (toggle.checked) { toggle.checked = false; toggle.dispatchEvent(new Event('change', { bubbles: true })); }
			});
			reportSelection = { selected: false, vin: '', confirmed: false };
			syncSelectionUi(dialog);
			lastTrigger?.setAttribute('aria-busy', 'true');
			confirmSelections(dialog);
			return;
		}
		closeImagePreview(dialog, false);
		if (!preserveSelectionsOnNextOpen) {
			reportSelection = { selected: false, vin: "", confirmed: false };
			reportRevision += 1;
			// This is a pre-cart choice, not a prebuilt kit. Start optional products off
			// so a customer never receives an assembly item they did not explicitly pick.
			const control = inputs();
			// Set the mode before clearing item toggles. The kit controller responds to
			// a mode change and can re-render its rows; clearing after that render keeps
			// every Product C option truly opt-in on every fresh open.
			setAssemblyMode("single");
			setChecked(control.clip, false);
			setChecked(control.paint, false);
			assemblyToggleInputs().forEach(function (toggle) {
				if (!toggle.checked) return;
				toggle.checked = false;
				toggle.dispatchEvent(new Event("change", { bubbles: true }));
			});
		}
		preserveSelectionsOnNextOpen = false;
		mountAssemblyOptions(dialog);
		const confirm = dialog.querySelector("[data-casper-package-confirm]");
		if (confirm) confirm.disabled = false;
		syncSelectionUi(dialog);
		if (typeof dialog.showModal === "function") dialog.showModal();
		else dialog.setAttribute("open", "");
		const focusTarget = dialog.querySelector("[data-casper-package-toggle]:not(:disabled)");
		if (focusTarget) focusTarget.focus();
		track("casper_package_modal_opened", { source: source || "add_to_cart" });
		if (reportAvailable()) track("casper_package_report_viewed", { source: source || "add_to_cart" });
	}

	function settleExternalModalRequest(result) {
		const request = externalModalRequest;
		if (!request) return;
		externalModalRequest = null;
		request.resolve(result);
	}

	function cancelActiveCommit(dialog) {
		if (!activeCommitAttempt || activeCommitAttempt.dialog !== dialog) return;
		window.clearTimeout(activeCommitAttempt.timeoutId);
		activeCommitAttempt = null;
	}

	function closeDialog(dialog, restore) {
		if (!dialog) return;
		if (restore) fitmentContinuationApproved = false;
		cancelActiveCommit(dialog);
		if (restore) restoreSelections(selectionSnapshot);
		if (restore) settleExternalModalRequest({ confirmed: false, addOns: [] });
		closeImagePreview(dialog, false);
		if (typeof dialog.close === "function" && dialog.open) dialog.close();
		else {
			dialog.removeAttribute("open");
			unmountAssemblyOptions(dialog);
			selectionSnapshot = null;
		}
	}

	function findAtcButton() {
		return Array.from(document.querySelectorAll(ATC_SELECTOR)).find(function (button) {
			const form = button.form || button.closest("form") || document.getElementById(button.getAttribute("form"));
			return form === document.getElementById("product-form");
		});
	}

	function isAtcButton(button) {
		if (!button || !button.matches || !button.matches(ATC_SELECTOR)) return false;
		const form = button.form || button.closest("form") || document.getElementById(button.getAttribute("form"));
		return form === document.getElementById("product-form");
	}

	function resumeNativeAdd(dialog, committedSelections) {
		const button = lastTrigger && isAtcButton(lastTrigger) ? lastTrigger : findAtcButton();
		if (!button) return;
		resumeNativeSubmission = true;
		window.setTimeout(function () {
			// The legacy kit handler reads #kit-items synchronously from the click. Keep
			// those rows mounted until that handler has taken its snapshot; moving them
			// home first can re-initialize the assembly controller and strand this modal
			// on "Adding to cart...".
			restoreSelections(committedSelections);
			button.click();
			window.setTimeout(function () {
				closeDialog(dialog, false);
				resumeNativeSubmission = false;
			}, 0);
		}, 0);
	}

	function readBaseCartItem() {
		const form = document.getElementById("product-form");
		const id = form && form.querySelector('[name="id"]');
		const quantity = form && form.querySelector('[name="quantity"]');
		return id && id.value
			? [{ id: id.value, quantity: Number(quantity && quantity.value) || 1 }]
			: [];
	}

	function guardMainCartItem(items) {
		const cartItems = Array.isArray(items) ? items.slice() : [];
		const mainItem = cartItems[0];
		if (!mainItem) {
			return { ok: false, reason: "missing-main-item", message: "The main part could not be added. Please try again." };
		}
		if (typeof window.__partifyBuildGuardedMainCartItem !== "function") {
			return { ok: false, reason: "main-item-guard-unavailable", message: "The main part could not be validated. Please try again." };
		}

		const guarded = window.__partifyBuildGuardedMainCartItem({
			form: document.getElementById("product-form"),
			variantId: mainItem.id,
			quantity: mainItem.quantity,
			properties: mainItem.properties,
			showError: false,
		});
		if (!guarded || !guarded.ok || !guarded.item) return guarded || { ok: false };

		cartItems[0] = guarded.item;
		return { ok: true, items: cartItems };
	}

	function commitModalCart(attempt) {
		// The modal owns this final submit. The prior synthetic click had to pass
		// through several legacy PDP handlers; on this page none of them completed
		// the request, leaving the modal permanently on "Adding to cart...". Build
		// the exact base, assembly, and Casper add-on lines, then send Shopify one
		// cart request and navigate to the cart only after it succeeds.
		const baseItems =
			typeof window.__partifyGetSelectedAssemblyCartItems === "function"
				? Promise.resolve(window.__partifyGetSelectedAssemblyCartItems())
				: Promise.resolve(readBaseCartItem());

		return baseItems
			.then(function (items) {
				const guarded = guardMainCartItem(items);
				if (!guarded.ok) return guarded;

				const casperItems =
					typeof window.__partifyGetSelectedCasperAddOns === "function"
						? Promise.resolve(window.__partifyGetSelectedCasperAddOns())
						: Promise.resolve([]);
				return casperItems.then(function (addOns) {
					const combinedItems = dedupeIntendedLines(
						guarded.items.concat(Array.isArray(addOns) ? addOns : []),
					);
					const associatedVin = String(
						guarded.items[0]?.properties?.VIN || guarded.vin || "",
					).trim().toUpperCase();
					if (associatedVin) {
						combinedItems.forEach(function (item) {
							if (item.properties?._vhr_schema === "1") return;
							item.properties = Object.assign({}, item.properties || {}, { VIN: associatedVin });
						});
					}
					return {
						ok: true,
						items: combinedItems,
					};
				});
			})
			.then(function (result) {
				if (!result.ok || !result.items || !result.items.length) return result;
				if (activeCommitAttempt !== attempt) return { ok: false, reason: "cancelled" };
				window.__partifyValidateVehicleHistoryAddOn?.(result.items);
				const paintPreferenceContext = window.PartifyVehiclePaintPreference?.captureVariant(result.items[0].id);
				return fetch("/cart/add.js", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify({ items: result.items }),
				}).then(function (response) {
					if (response.ok) {
						window.PartifyVehiclePaintPreference?.confirm(paintPreferenceContext);
						return { ok: true, items: result.items };
					}
					return response.json().catch(function () { return {}; }).then(function (body) {
						const description = body && body.description;
						return {
							ok: false,
							reason: "cart-add-failed",
							message: typeof description === "string" && description.trim()
								? description
								: "We couldn't add this selection. Please review your cart before trying again.",
						};
					});
				});
			});
	}

	function goToCart(dialog) {
		closeDialog(dialog, false);
		if (window.__partifyAddOnStyles?.afterAdd({ items: completedCartItems })) return;
		if (typeof window.__PARTIFY_CASPER_ADD_ON_MODAL_ON_CART_SUCCESS === "function") {
			window.__PARTIFY_CASPER_ADD_ON_MODAL_ON_CART_SUCCESS();
			return;
		}
		window.location.assign("/cart");
	}

	function confirmSelections(dialog) {
		const confirm = dialog.querySelector("[data-casper-package-confirm]");
		const notice = dialog.querySelector("[data-casper-package-notice]");
		if (confirm && confirm.disabled) return;
		if (reportError()) {
			syncReportUi(dialog);
			dialog.querySelector(/^[A-HJ-NPR-Z0-9]{17}$/.test(reportSelection.vin) ? "[data-casper-report-confirm]" : "[data-casper-report-vin]")?.focus();
			return;
		}
		const control = inputs();
		const hasAssembly = selectedAssemblyCount() > 0;
		const hasAddOns = !!((control.clip && control.clip.checked) || (control.paint && control.paint.checked) || reportSelection.selected);
		setAssemblyMode(hasAssembly ? "kit" : "single");
		if (confirm) {
			confirm.disabled = true;
			confirm.textContent = "Adding to cart...";
		}

		if (externalModalRequest) {
			const request = externalModalRequest;
			Promise.all([
				Promise.resolve(
					typeof window.__partifyGetSelectedCasperAddOns === "function"
						? window.__partifyGetSelectedCasperAddOns()
						: [],
				),
				Promise.resolve(
					typeof window.__partifyGetSelectedAssemblyCartItems === "function"
						? window.__partifyGetSelectedAssemblyCartItems()
						: [],
				),
			])
				.then(function (selectedItems) {
					if (externalModalRequest !== request) return;
					const addOns = Array.isArray(selectedItems[0]) ? selectedItems[0] : [];
					const assemblyItems = Array.isArray(selectedItems[1])
						? selectedItems[1].slice(1)
						: [];
					window.__partifyValidateVehicleHistoryAddOn?.(addOns);
					preserveSelectionsOnNextOpen = true;
					track("casper_package_saved", {
						report: reportSelection.selected,
						source: request.source,
						clip: !!(control.clip && control.clip.checked),
						paint: !!(control.paint && control.paint.checked),
						assembly_items: selectedAssemblyCount(),
					});
					settleExternalModalRequest({
						confirmed: true,
						addOns: dedupeIntendedLines(addOns.concat(assemblyItems)),
					});
					closeDialog(dialog, false);
				})
				.catch(function (error) {
					if (externalModalRequest !== request) return;
					if (reportSelection.selected) {
						if (confirm) confirm.disabled = false;
						if (notice) notice.textContent = error.message || reportText("retry");
						return;
					}
					settleExternalModalRequest({ confirmed: false, addOns: [] });
					closeDialog(dialog, true);
				});
			return;
		}

		const proceed = function (confirmed, failureMessage, failureReason) {
			if (!confirmed) {
				if (window.__partifyAddOnStyles?.active() && !dialog.open && failureReason !== 'missing-vin') dialog.showModal();
				if (failureReason === "missing-vin") {
					closeDialog(dialog, true);
					window.setTimeout(function () {
						if (typeof window.__partifyShowMissingMainVinError === "function") {
							window.__partifyShowMissingMainVinError();
						}
					}, 0);
					return;
				}
				if (confirm) {
					confirm.disabled = false;
					syncSelectionUi(dialog);
				}
				// syncSelectionUi restores the normal item-count message, so apply the
				// recovery message afterwards and keep it visible until the customer
				// changes a selection.
				if (notice) {
					notice.setAttribute("data-cart-error", "");
					notice.textContent =
						failureMessage || (reportSelection.selected ? reportText("retry") : "Your cart was not changed. Review your selections and try again.");
					notice.focus();
				}
				return;
			}
			preserveSelectionsOnNextOpen = true;
			track("casper_package_saved", {
				report: reportSelection.selected,
				clip: !!(control.clip && control.clip.checked),
				paint: !!(control.paint && control.paint.checked),
				assembly_items: selectedAssemblyCount(),
			});
			goToCart(dialog);
		};

		if (fitmentGateWillBlock()) {
			// Fitment's own modal must run before any cart request is made.
			resumeNativeAdd(dialog, captureSelections());
			return;
		}

		const attempt = {
			dialog: dialog,
			timeoutId: null,
		};
		activeCommitAttempt = attempt;
		const finishAttempt = function (confirmed, failureMessage, failureReason) {
			// Closing the modal (or timing out) cancels this attempt. A late response
			// must never resume the native add-to-cart flow in the background.
			if (activeCommitAttempt !== attempt) return;
			window.clearTimeout(attempt.timeoutId);
			activeCommitAttempt = null;
			lastTrigger?.removeAttribute('aria-busy');
			proceed(confirmed, failureMessage, failureReason);
		};

		attempt.timeoutId = window.setTimeout(function () {
			finishAttempt(
				false,
				reportSelection.selected ? reportText("retry") : "We couldn't finish adding the optional items in time. Nothing was added—please try again.",
			);
		}, addOnCommitTimeoutMs());

		Promise.resolve()
			.then(function () {
				return commitModalCart(attempt);
			})
			.then(function (result) {
				if (result?.ok) completedCartItems = result.items || [];
				finishAttempt(!!(result && result.ok), result && result.message, result && result.reason);
			})
			.catch(function () {
				finishAttempt(false);
			});
	}

	function openPdpDialog(trigger) {
		fitmentContinuationApproved = !!window.__pdpFitmentGateConfirming;
		if (fitmentGateWillBlock() && typeof window.__pdpFitmentGateRequestResolution === "function") {
			window.__pdpFitmentGateRequestResolution(function () {
				fitmentContinuationApproved = true;
				openDialog(trigger);
			});
			return;
		}
		openDialog(trigger);
	}

	function bindEvents(dialog) {
		bindReportEvents(dialog);
		dialog.querySelectorAll("[data-casper-package-close]").forEach(function (button) {
			button.addEventListener("click", function () {
				closeDialog(dialog, true);
			});
		});

		dialog.querySelectorAll("[data-casper-package-toggle]").forEach(function (toggle) {
			toggle.addEventListener("change", function () {
				setChecked(inputs()[toggle.dataset.casperPackageToggle], toggle.checked);
				syncSelectionUi(dialog);
				track("casper_package_toggle_changed", {
					product: toggle.dataset.casperPackageToggle,
					selected: toggle.checked,
				});
			});
		});

		dialog.addEventListener(
			"click",
			function (event) {
				const target = event.target;
				if (!(target instanceof Element)) return;
				const preview = dialog.querySelector("[data-casper-package-image-preview]");
				if (preview && !preview.hidden) {
					if (target.closest("[data-casper-package-image-preview-close]")) {
						event.preventDefault();
						event.stopPropagation();
						closeImagePreview(dialog, true);
						return;
					}
					if (target.closest("[data-casper-package-image-preview-previous]")) {
						event.preventDefault();
						event.stopPropagation();
						moveImagePreview(dialog, -1);
						return;
					}
					if (target.closest("[data-casper-package-image-preview-next]")) {
						event.preventDefault();
						event.stopPropagation();
						moveImagePreview(dialog, 1);
						return;
					}
					if (target === preview) {
						event.preventDefault();
						event.stopPropagation();
						closeImagePreview(dialog, true);
					}
					return;
				}

				const packageMedia = target.closest("[data-casper-package-media]");
				if (packageMedia && dialog.contains(packageMedia)) {
					const card = packageMedia.closest("[data-casper-product-card], [data-casper-report-card]");
					const heading = card && card.querySelector(".casper-package-modal__product-copy strong");
					event.preventDefault();
					event.stopPropagation();
					openImagePreview(dialog, packageMedia.querySelectorAll("img"), (heading && heading.textContent) || "Product image", packageMedia);
					return;
				}

				const assemblyImage = target.closest("#kit-items .kit-item__thumb img");
				if (assemblyImage && dialog.contains(assemblyImage)) {
					const item = assemblyImage.closest(".kit-item");
					const title = item && item.querySelector(".kit-item__title");
					event.preventDefault();
					event.stopPropagation();
					openImagePreview(dialog, [assemblyImage], (title && title.textContent) || "Assembly part", assemblyImage);
				}
			},
			true,
		);

		dialog.addEventListener("click", function (event) {
			if (event.target === dialog) closeDialog(dialog, true);
		});
		dialog.addEventListener("cancel", function (event) {
			const preview = dialog.querySelector("[data-casper-package-image-preview]");
			if (preview && !preview.hidden) {
				event.preventDefault();
				closeImagePreview(dialog, true);
				return;
			}
			event.preventDefault();
			closeDialog(dialog, true);
		});
		dialog.addEventListener("keydown", function (event) {
			const preview = dialog.querySelector("[data-casper-package-image-preview]");
			if (!preview || preview.hidden) return;
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				moveImagePreview(dialog, -1);
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				moveImagePreview(dialog, 1);
			}
		});
		dialog.addEventListener("close", function () {
			closeImagePreview(dialog, false);
			unmountAssemblyOptions(dialog);
			selectionSnapshot = null;
			if (lastTrigger && typeof lastTrigger.focus === "function") lastTrigger.focus();
		});

		const confirm = dialog.querySelector("[data-casper-package-confirm]");
		if (confirm) confirm.addEventListener("click", function () { confirmSelections(dialog); });
		dialog.addEventListener("change", function (event) {
			if (event.target && event.target.matches("#kit-items .kit-item__toggle-input")) {
				window.setTimeout(function () { syncSelectionUi(dialog); }, 0);
			}
		});

		document.addEventListener("click", function (event) {
			if (resumeNativeSubmission) return;
			const target = event.target instanceof Element && event.target.closest(ATC_SELECTOR);
			if (!isAtcButton(target) || target.disabled) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			openPdpDialog(target);
		}, true);

		const form = document.getElementById("product-form");
		if (form) {
			form.addEventListener("submit", function (event) {
				if (resumeNativeSubmission) return;
				event.preventDefault();
				event.stopImmediatePropagation();
				openPdpDialog(findAtcButton());
			}, true);
		}

		document.addEventListener("change", function (event) {
			const target = event.target;
			if (target && (target.matches('[data-casper-bundle] [data-item-input]') || target.matches('input[name="bundle_mode"], #variant-selector, input[name="select_paint_option"]'))) {
				window.setTimeout(function () { syncSelectionUi(dialog); }, 0);
			}
		});
		document.addEventListener("variantsUpdated", function () { window.setTimeout(function () { syncSelectionUi(dialog); }, 0); });
		document.addEventListener("cart:refresh", function () { window.setTimeout(function () { syncSelectionUi(dialog); }, 0); });

		const kitItems = document.getElementById("kit-items");
		if (kitItems && typeof MutationObserver !== "undefined") {
			new MutationObserver(function () { syncSelectionUi(dialog); }).observe(kitItems, { childList: true, subtree: true });
		}
		const legacyBundle = document.querySelector("[data-casper-bundle]");
		if (legacyBundle && typeof MutationObserver !== "undefined") {
			new MutationObserver(function () { syncSelectionUi(dialog); }).observe(legacyBundle, {
				attributes: true,
				subtree: true,
				attributeFilter: ["disabled"],
			});
		}
	}

	function injectStyles() {
		if (document.getElementById(STYLE_ID)) return;
		const style = document.createElement("style");
		style.id = STYLE_ID;
		style.textContent = [
			".casper-package-modal [data-casper-report-card][hidden]{display:none}",
			".casper-package-modal__report-details{grid-column:1/-1;color:#5d7192;font-size:13px;line-height:1.5}.casper-package-modal__report-details p{margin:4px 0}.casper-package-modal__report-details [hidden]{display:none}.casper-package-modal__report-details #casper-report-fields{margin-top:16px}.casper-package-modal__report-details label{color:#102a52}.casper-package-modal__report-details input[type=text]{display:block;width:100%;max-width:420px;margin:7px 0;padding:12px;border:1px solid #b9c9e4;border-radius:8px;font-size:16px;letter-spacing:.08em}.casper-package-modal__report-confirm{display:flex;align-items:flex-start;gap:10px;margin-top:14px}.casper-package-modal__report-confirm input{width:18px;height:18px;flex:0 0 18px;margin-top:2px}.casper-package-modal__report-details [data-casper-report-error]{color:#a32222;margin-top:8px}",
			".casper-package-modal,.casper-package-modal *{box-sizing:border-box}.casper-package-modal button,.casper-package-modal input{font:inherit}",
			".casper-package-modal{width:min(1180px,calc(100vw - 32px));height:min(900px,calc(100vh - 28px));max-height:calc(100vh - 28px);margin:auto;padding:0;border:0;border-radius:24px;background:#fff;color:#10213e;box-shadow:0 28px 80px rgba(11,31,67,.36)}.casper-package-modal::backdrop{background:rgba(10,25,54,.62);backdrop-filter:blur(4px)}",
			".casper-package-modal__panel{display:grid;grid-template-rows:auto minmax(0,1fr) auto;height:100%;overflow:hidden}.casper-package-modal__header{position:relative;padding:34px 42px 30px;background:linear-gradient(125deg,#0b2554,#164795);color:#fff}.casper-package-modal__header>div>span,.casper-package-modal__section-heading>div>span{display:block;margin-bottom:7px;color:#bdd4ff;font-size:11px;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.casper-package-modal__header h2{margin:0;color:#fff;font-size:35px;line-height:1.06;letter-spacing:-.045em}.casper-package-modal__header p{max-width:60ch;margin:12px 44px 0 0;color:#dbe8ff;font-size:15px;line-height:1.5}",
			".casper-package-modal__close{position:absolute;top:18px;right:18px;display:flex;align-items:center;justify-content:center;width:38px;height:38px;padding:0;border:1px solid rgba(255,255,255,.34);border-radius:50%;background:rgba(255,255,255,.1);color:#fff;font-size:28px;line-height:1;cursor:pointer}.casper-package-modal__close:hover{background:rgba(255,255,255,.2)}.casper-package-modal button:focus-visible,.casper-package-modal input:focus-visible{outline:3px solid #f3c542;outline-offset:3px}",
			".casper-package-modal__body{min-height:0;overflow-y:auto;padding:28px 42px 20px;background:#fff}.casper-package-modal__section-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;margin:0 0 13px}.casper-package-modal__section-heading h3{margin:0;color:#102a52;font-size:22px;letter-spacing:-.03em}.casper-package-modal__section-heading p{margin:5px 0 0;color:#5d7192;font-size:14px;line-height:1.45}.casper-package-modal__section-heading>div>span{color:#42638f}",
			".casper-package-modal__products{display:grid;gap:12px}.casper-package-modal__product-card{display:grid;grid-template-columns:112px minmax(0,1fr) auto;gap:20px;align-items:center;min-height:132px;padding:16px 20px;border:1px solid #dce5f3;border-radius:16px;background:#fff;transition:border-color .16s,box-shadow .16s}.casper-package-modal__product-card:hover{border-color:#4e72c9;box-shadow:0 4px 14px rgba(30,75,165,.1)}.casper-package-modal__product-card.is-unavailable{opacity:.6;background:#fafbfc}",
			".casper-package-modal__media{display:flex;align-items:center;justify-content:center;width:112px;height:96px;padding:7px;border:1px solid #e5ebf5;border-radius:13px;background:#f8fafc;color:#1748a4;overflow:hidden;cursor:zoom-in;transition:border-color .16s,box-shadow .16s,transform .16s}.casper-package-modal__media:hover{border-color:#4e72c9;box-shadow:0 5px 14px rgba(23,72,164,.16);transform:scale(1.035)}.casper-package-modal__media img{width:100%;height:100%;object-fit:contain;mix-blend-mode:multiply}.casper-package-modal__media.is-fallback svg{width:40px;height:40px}",
			".casper-package-modal__product-copy{display:flex;min-width:0;flex-direction:column;gap:5px}.casper-package-modal__badge{display:inline-flex;align-self:flex-start;padding:4px 7px;border-radius:99px;background:#e6efff;color:#224b9e;font-size:10px;font-weight:800;line-height:1;letter-spacing:.04em;text-transform:uppercase}.casper-package-modal__product-copy strong{font-size:19px;line-height:1.25;letter-spacing:-.02em}.casper-package-modal__product-copy>span:last-child{color:#627493;font-size:14px;line-height:1.42}.casper-package-modal__product-actions{display:flex;align-items:center;justify-content:flex-end;gap:18px;min-width:136px;color:#123b87;font-size:17px;white-space:nowrap}",
			".casper-package-modal__toggle{display:inline-flex;position:relative;flex:0 0 auto;width:48px;height:27px;cursor:pointer}.casper-package-modal__toggle input{position:absolute;inline-size:1px;block-size:1px;margin:-1px;opacity:0}.casper-package-modal__toggle span{position:relative;display:block;width:48px;height:27px;border-radius:99px;background:#c7d2e3;transition:background .16s}.casper-package-modal__toggle span::after{position:absolute;top:4px;left:4px;width:19px;height:19px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(7,30,70,.28);content:'';transition:transform .16s}.casper-package-modal__toggle input:checked+span{background:#16a34a}.casper-package-modal__toggle input:checked+span::after{transform:translateX(21px)}.casper-package-modal__toggle input:disabled+span{cursor:not-allowed;background:#d6dde8}",
			".casper-package-modal__assembly{margin-top:24px;padding:20px;border:1px solid #c9d9f3;border-radius:16px;background:#f7faff}.casper-package-modal__assembly[hidden]{display:none}.casper-package-modal__assembly-count{padding:7px 10px;border-radius:99px;background:#e3edff;color:#244c9a;font-size:12px;font-weight:800;white-space:nowrap}.casper-package-modal__assembly-items .kit-items{display:grid!important;gap:10px;margin:0!important;padding:0!important;border:0!important;background:transparent!important;box-shadow:none!important}.casper-package-modal__assembly-items .kit-items__label{grid-column:1/-1;margin:0 0 2px;color:#42638f;font-size:14px}.casper-package-modal__assembly-items .kit-item{display:flex!important;align-items:center;justify-content:space-between;gap:18px;margin:0!important;padding:14px!important;border:1px solid #dce5f3!important;border-radius:14px!important;background:#fff!important}.casper-package-modal__assembly-items .kit-item__left{display:flex;align-items:center;gap:15px;min-width:0}.casper-package-modal__assembly-items .kit-item__thumb{width:82px!important;height:82px!important;flex:0 0 82px}.casper-package-modal__assembly-items .kit-item__thumb img{width:100%;height:100%;object-fit:contain;cursor:zoom-in}.casper-package-modal__assembly-items .kit-item__title{max-width:none!important;font-size:16px}.casper-package-modal__assembly-items .kit-item__right{display:flex;align-items:center;gap:15px}.casper-package-modal__assembly-items .kit-item__price{font-size:16px}.casper-package-modal__assembly-items .kit-item__toggle-pill{width:48px!important;height:27px!important}.casper-package-modal__assembly-items .kit-item__toggle-pill::after{width:19px!important;height:19px!important}.casper-package-modal__assembly-items .kit-item__toggle-input:checked+.kit-item__toggle-pill::after{transform:translateX(21px)!important}",
			".casper-package-modal__notice{min-height:20px;margin:20px 0 0;color:#5d7192;font-size:13px;line-height:1.4}.casper-package-modal__footer{display:flex;justify-content:flex-end;gap:10px;padding:18px 34px 22px;border-top:1px solid #e8edf5;background:#fff}.casper-package-modal__secondary,.casper-package-modal__primary{min-height:45px;padding:0 18px;border-radius:10px;font-size:14px;font-weight:800;cursor:pointer}.casper-package-modal__secondary{border:1px solid #cbd6e6;background:#fff;color:#29476e}.casper-package-modal__primary{border:1px solid #1748a4;background:#1748a4;color:#fff;box-shadow:0 5px 12px rgba(23,72,164,.2)}.casper-package-modal__primary:disabled{cursor:wait;border-color:#bdc9df;background:#bdc9df;box-shadow:none}",
			".casper-package-modal__footer{flex-wrap:wrap}.casper-package-modal__footer .casper-package-modal__notice{flex:0 0 100%;margin:0}.casper-package-modal__notice[data-cart-error]{color:#a32222;font-weight:700}",
			".casper-package-modal__image-preview{position:fixed;z-index:20;inset:0;display:grid;place-items:center;padding:24px;background:rgba(5,19,45,.77);backdrop-filter:blur(7px)}.casper-package-modal__image-preview[hidden]{display:none}.casper-package-modal__image-preview-card{width:min(920px,calc(100vw - 48px));max-height:calc(100vh - 48px);overflow:hidden;border:1px solid rgba(255,255,255,.25);border-radius:22px;background:#fff;box-shadow:0 28px 80px rgba(0,0,0,.45)}.casper-package-modal__image-preview-heading{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:16px 18px 16px 22px;background:linear-gradient(125deg,#0b2554,#164795);color:#fff;font-size:16px;font-weight:800}.casper-package-modal__image-preview-close{display:flex;align-items:center;justify-content:center;flex:0 0 38px;width:38px;height:38px;padding:0;border:1px solid rgba(255,255,255,.35);border-radius:50%;background:rgba(255,255,255,.1);color:#fff;font-size:27px;line-height:1}.casper-package-modal__image-preview-stage{display:grid;grid-template-columns:54px minmax(0,1fr) 54px;align-items:center;gap:14px;min-height:clamp(360px,65vh,660px);padding:18px;background:#f4f7fc}.casper-package-modal__image-preview-stage img{display:block;width:100%;height:min(620px,calc(100vh - 190px));object-fit:contain;mix-blend-mode:multiply}.casper-package-modal__image-preview-nav{display:flex;align-items:center;justify-content:center;width:46px;height:46px;padding:0;border:1px solid #b9c9e4;border-radius:50%;background:#fff;color:#173d8d;font-size:23px;font-weight:800;box-shadow:0 3px 10px rgba(16,42,82,.12);cursor:pointer}.casper-package-modal__image-preview-nav[hidden]{visibility:hidden}.casper-package-modal__image-preview-count{min-height:20px;margin:0;padding:10px 22px 14px;background:#fff;color:#56708f;font-size:13px;font-weight:700;text-align:center}",
			"@media (max-width:600px){.casper-package-modal{width:calc(100vw - 12px);height:calc(100vh - 12px);max-height:calc(100vh - 12px);border-radius:18px}.casper-package-modal__header{padding:24px 22px 22px}.casper-package-modal__header h2{font-size:28px}.casper-package-modal__header p{font-size:14px}.casper-package-modal__body{padding:20px 16px}.casper-package-modal__section-heading h3{font-size:20px}.casper-package-modal__product-card{grid-template-columns:68px minmax(0,1fr);gap:13px;min-height:0;padding:13px}.casper-package-modal__media{width:68px;height:68px}.casper-package-modal__product-copy strong{font-size:16px}.casper-package-modal__product-copy>span:last-child{font-size:12px}.casper-package-modal__product-actions{grid-column:2;justify-content:space-between;min-width:0;font-size:15px}.casper-package-modal__assembly{margin-top:18px;padding:14px}.casper-package-modal__assembly-count{font-size:11px}.casper-package-modal__assembly-items .kit-item{gap:10px;padding:11px!important}.casper-package-modal__assembly-items .kit-item__left{gap:10px}.casper-package-modal__assembly-items .kit-item__thumb{width:58px!important;height:58px!important;flex-basis:58px}.casper-package-modal__assembly-items .kit-item__title{font-size:14px}.casper-package-modal__assembly-items .kit-item__right{gap:9px}.casper-package-modal__assembly-items .kit-item__price{font-size:14px}.casper-package-modal__footer{padding:14px 16px 17px}.casper-package-modal__footer button{flex:1;padding:0 10px}.casper-package-modal__image-preview{padding:12px}.casper-package-modal__image-preview-card{width:calc(100vw - 24px);max-height:calc(100vh - 24px);border-radius:17px}.casper-package-modal__image-preview-heading{padding:13px 14px 13px 17px;font-size:14px}.casper-package-modal__image-preview-close{flex-basis:34px;width:34px;height:34px;font-size:24px}.casper-package-modal__image-preview-stage{grid-template-columns:40px minmax(0,1fr) 40px;gap:5px;min-height:0;padding:10px}.casper-package-modal__image-preview-stage img{height:calc(100vh - 190px)}.casper-package-modal__image-preview-nav{width:36px;height:36px;font-size:19px}.casper-package-modal__image-preview-count{padding:8px 14px 11px}}",
		].join("");
		document.head.appendChild(style);
	}

	function mount() {
		if (initialized || document.getElementById(DIALOG_ID)) return true;
		const legacy = document.querySelector("[data-casper-bundle]");
		const control = inputs();
		if (!legacy || !control.clip) return false;
		initialized = true;
		injectStyles();
		document.body.appendChild(buildDialog());
		const dialog = document.getElementById(DIALOG_ID);
		rememberDescriptions(dialog);
		hideSourceSections();
		bindEvents(dialog);
		track("casper_package_checkout_prompt_ready", {
			has_assembly: !!control.kit,
			has_touchup: !!control.paint,
		});
		return true;
	}

	function waitForSource(attempt) {
		if (mount()) return;
		if (attempt >= 80) return;
		window.setTimeout(function () { waitForSource(attempt + 1); }, 100);
	}

	window.__partifyRequestCasperAddOnModal = function (options) {
		if (window.__partifyAddOnStyles?.active()) return Promise.resolve({ confirmed: true, addOns: [] });
		if (!mount()) return Promise.resolve({ confirmed: true, addOns: [] });
		const dialog = document.getElementById(DIALOG_ID);
		if (!dialog || dialog.open || externalModalRequest) {
			return Promise.resolve({ confirmed: false, addOns: [] });
		}
		preserveSelectionsOnNextOpen = true;
		return new Promise(function (resolve) {
			externalModalRequest = {
				resolve: resolve,
				source: String((options && options.source) || "external"),
			};
			openDialog(document.activeElement, externalModalRequest.source);
		});
	};

	waitForSource(0);
})();
